<template>
  <div class="lang-switcher" ref="rootEl">
    <button
      type="button"
      class="lang-switcher__button"
      :class="{ 'lang-switcher__button--compact': compact, 'lang-switcher__button--loading': localeStore.loading }"
      :aria-label="$t('common.selectLanguage')"
      :title="$t('common.interfaceLanguage')"
      :aria-busy="localeStore.loading"
      aria-haspopup="listbox"
      :aria-expanded="open"
      @click="toggle"
    >
      <KohIcon name="globe" :size="compact ? 'sm' : 'md'" />
      <span class="lang-switcher__code">{{ localeStore.current.short }}</span>
    </button>

    <ul
      v-if="open"
      class="lang-switcher__menu"
      role="listbox"
      :aria-label="$t('common.selectLanguage')"
    >
      <li
        v-for="lang in UI_LOCALES"
        :key="lang.code"
        class="lang-switcher__option"
        :class="{ 'lang-switcher__option--active': lang.code === localeStore.locale }"
        role="option"
        :aria-selected="lang.code === localeStore.locale"
        @click="select(lang.code)"
      >
        <span class="lang-switcher__native">{{ lang.native }}</span>
        <KohIcon
          v-if="lang.code === localeStore.locale"
          name="check"
          size="sm"
          class="lang-switcher__check"
        />
      </li>
    </ul>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useLocaleStore } from '../stores/locale'
import { UI_LOCALES } from '../locales'
import KohIcon from './KohIcon.vue'

defineProps({
  compact: { type: Boolean, default: false },
})

const localeStore = useLocaleStore()
const open = ref(false)
const rootEl = ref(null)

function toggle() {
  open.value = !open.value
}

function select(code) {
  // Fire-and-forget: setLocale() lazy-loads the locale chunk if needed.
  // The button shows a busy state (via localeStore.loading) while it's
  // in flight; we don't block closing the dropdown on it.
  localeStore.setLocale(code)
  open.value = false
}

function onClickOutside(event) {
  if (rootEl.value && !rootEl.value.contains(event.target)) {
    open.value = false
  }
}

function onKeydown(event) {
  if (event.key === 'Escape') {
    open.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', onClickOutside)
  document.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onClickOutside)
  document.removeEventListener('keydown', onKeydown)
})
</script>

<style scoped>
.lang-switcher {
  position: relative;
  display: inline-flex;
}

.lang-switcher__button {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.9rem;
  border-radius: var(--koh-radius-pill, 999px);
  border: 1px solid var(--koh-border-subtle, rgba(0, 0, 0, 0.08));
  background: var(--koh-bg-card, #fff);
  color: var(--koh-text-primary, #333);
  font-family: var(--koh-font-ui);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s, border-color 0.2s;
  line-height: 1;
}

.lang-switcher__button:hover {
  background: var(--koh-bg-card-muted, #f8f9fa);
  transform: translateY(-1px);
}

.lang-switcher__button--loading {
  opacity: 0.6;
  cursor: wait;
}

.lang-switcher__button--compact {
  padding: 0.55rem;
  min-width: 2.5rem;
  min-height: 2.5rem;
  justify-content: center;
}

.lang-switcher__code {
  white-space: nowrap;
  letter-spacing: 0.02em;
}

.lang-switcher__menu {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  z-index: 50;
  margin: 0;
  padding: 0.35rem;
  list-style: none;
  min-width: 11rem;
  border-radius: var(--koh-radius-bento, 12px);
  border: 1px solid var(--koh-border-subtle, rgba(0, 0, 0, 0.08));
  background: var(--koh-bg-card, #fff);
  box-shadow: var(--koh-shadow-card-lg, 0 10px 30px rgba(0, 0, 0, 0.15));
}

.lang-switcher__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.55rem 0.7rem;
  border-radius: var(--koh-radius-btn, 8px);
  font-family: var(--koh-font-ui);
  font-size: 0.9rem;
  color: var(--koh-text-primary, #333);
  cursor: pointer;
  white-space: nowrap;
}

.lang-switcher__option:hover {
  background: var(--koh-bg-card-muted, #f8f9fa);
}

.lang-switcher__option--active {
  font-weight: 700;
  color: var(--koh-text-accent);
}

.lang-switcher__check {
  flex-shrink: 0;
  color: var(--koh-text-accent);
}
</style>
